/**!
 *  Skill-building Activities widget.
 *  Author: Bjorn Tollstrom <bjorn@rodolfo.se>
 */

import React from 'react';
import Widget from '../../widget.js';
import styles from './skillbuildingactivities.module.scss';
import API from 'Class/API';
import Auth from 'Class/Auth';
import Fuse from 'Class/Fuse';
import Globals from 'Class/Globals';
import FilterField from 'Components/UI/Field/FilterField';
import IconItem from 'Components/UI/IconItem';
import Preview from 'Components/Layout/Preview'
import SkillActivityStatus from 'Components/UI/SkillActivityStatus'
import Spinner from 'Components/Feedback/Spinner';
import WidgetWrapper from 'Components/UI/WidgetWrapper';
import TextField from 'Components/UI/Field/TextField';
import User from 'Components/UI/User';
import {TimeSince} from 'Functions';

class WidgetSkillBuildingActivities extends Widget
{
    constructor(props)
    {
        super(props);
        this.Fields = this.SetFields({
            title:
            {
                default: 'Skill-building Activities',
                insert: true,
                label: 'Title',
                reset: true,
                type: 'text',
                translate: true
            }
        }, {
            backgroundColor: 'transparent',
            textColor: 'black'
        }, true);
        this.FilterTimeout = null;
        this.LoadingUsers = {};
        this.Mounted = false;
        this.Name = 'Skill-building Activities';
        this.Translate =
        {
            activities: 'Skill-building Activities',
            cancelled: '%s left %a',
            close: 'Close',
            completed: '%s completed %a',
            created: 'New activity: %a',
            error: 'Something went wrong',
            info: 'Status',
            infotitle: '%s',
            loading: 'Loading...',
            loading_user: 'Loading user...',
            more: '+ %s more are working on this skill-building activity',
            nomore: 'is working on this skill-building activity',
            nocontent_feed: 'No activities',
            nocontent_specialization: 'No skill-building activities',
            not_started: '%s joined %a',
            nosubscriptions: 'No one is working on this skill-building activity yet',
            noone: 'No one is working on this skill-building activity yet',
            onlyyou: 'are working on this skill-building activity',
            paused: '%s paused %a',
            readmore: 'Read more',
            search_feed: 'Search for activities...',
            search_specialization: 'Search for skill-building activities...',
            started: '%s started %a',
            status_completed: 'Completed',
            status_not_started: 'Not started',
            status_paused: 'Paused',
            status_started: 'Started',
            subscriptions: 'Working on this skill-building activity',
            topics: 'Topics',
            unnamed: 'Unnamed technical skill',
            unknown: 'Unknown user',
            you: 'You',
            yourstatus: 'Your status:'
        };
        this.state =
        {
            dialog: null,
            error: false,
            errors: {},
            feed: [],
            filter: '',
            loading: false,
            scrollView: false,
            skills: {},
            statuses: {},
            users: {}
        };
    }

    /**
     * Load activities on mount.
     * @return void
     */

    componentDidMount()
    {
        this.Mounted = true;
        this.Reload();
    }

    /**
     * Update the overlay dialog when the activity feed changes.
     * @return void
     */

    componentDidUpdate()
    {
        const {contextId, token} = this.props;
        const {dialog, errors} = this.state;
        if (!dialog)
        {
            return;
        }
        
        const [dialogId, activityId] = dialog;
        const data = this.ExtractActivityFromFeed(activityId);
        if (!data)
        {
            return;
        }
        Globals.DialogUpdate(dialogId, {
            className: `Widget${token}`,
            props: {
                ...data,
                community: contextId,
                error: errors[data.id],
                getTranslation: this.GetTranslation,
                onClose: this.OnCloseDialog,
                onStatus: this.OnStatusChange
            },
            sticky: true,
            title: this.GetTranslation('infotitle').replace('%s', data.skill_name || this.GetTranslation('unnamed')),
            type: 'skillActivity'
        });
    }

    /**
     * Register unmount.
     * @return void
     */

    componentWillUnmount()
    {
        this.Mounted = false;
    }

    /**
     * Parse event slug into verbose string.
     * @param string slug - Event slug.
     * @param string subject - User id of the subject.
     * @param string activity - Activity name.
     * @return string - Verbose event string.
     */

    EventString = (slug, subject, activity) =>
    {
        const user = subject ? this.GetUser(subject) : true;

        if (!user)
        {
            return this.GetTranslation('loading_user');
        }

        switch (slug)
        {
            case 'cancelled':
                return this.GetTranslation('cancelled').replace('%s', user).replace('%a', activity);
            case 'completed':
                return this.GetTranslation('completed').replace('%s', user).replace('%a', activity);
            case 'created':
                return this.GetTranslation('created').replace('%s', user).replace('%a', activity);
            case 'not_started':
                return this.GetTranslation('not_started').replace('%s', user).replace('%a', activity);
            case 'paused':
                return this.GetTranslation('paused').replace('%s', user).replace('%a', activity);
            case 'started':
                return this.GetTranslation('started').replace('%s', user).replace('%a', activity);
            default:
                return '';
        }
    }

    /*
     * Extract a given activity from the feed.
     * @param int activityId - Activity ID.
     * @return object|null - Activity object or null if not found.
     */

    ExtractActivityFromFeed = (activityId) =>
    {
        const {feed} = this.state;
        for (let i = 0; i < feed.length; i++)
        {
            const item = feed[i];
            if (item.id === activityId)
            {
                return item;
            }
        }
        return null;
    }

    /*
     * Get user by ID from the Fuse API.
     * @param string id - User ID.
     * @param boolean load - Whether to load the user if not found.
     * @return string|false - User name or false if loading.
     */

    GetUser = (id, load = true, first = false) =>
    {
        const {users} = this.state;
        if (parseInt(id, 10) === Auth.UserId)
        {
            return this.GetTranslation('you');
        }
        if (this.LoadingUsers[id] === true || (!load && users[id] === undefined))
        {
            return false;
        }
        if (users[id] !== undefined)
        {
            return first ? users[id][1] : users[id][0];
        }

        this.LoadingUsers[id] = true;

        Fuse.Content(id, 'user', content =>
        {
            if (!this.Mounted)
            {
                return;
            }
            const {users} = this.state;
            if (!content)
            {
                users[id] = [this.GetTranslation('unknown'), this.GetTranslation('unknown')];
            }
            else
            {
                users[id] = [content.name, content.given_name || content.name.split(' ')[0]];
            }
            this.LoadingUsers[id] = false;
            this.setState({users});
        });
        return false;
    }

    /**
     * Render feed item.
     * @return JSX.Element - Feed item element.
     */

    Item = (data, index) =>
    {
        const {contextId, variant = 'feed'} = this.props;
        const {scrollView} = this.state;
        const {
            highlight,
            id,
            image,
            link,
            name,
            skill_name,
            skill_type,
            subject,
            subscriptions,
            type,
            updated
        } = data;
        const {errors, statuses} = this.state;
        const hasError = errors[id];
        const user = parseInt(highlight, 10);
        const isCurrentSubject = parseInt(subject, 10) === Auth.UserId;
        const isCurrentUser = user === Auth.UserId;
        const userName = user ? this.GetUser(user, variant !== 'feed', true) : null;
        const more = subscriptions - 1;
        const href = Array.isArray(link) && Array.isArray(link[0]) ? Fuse.ContentUrl(...link[0]) : link;
        const [overrideStatus, overrideUpdated] = statuses[id] || [null, null];

        return (
            <div className={styles.item} key={index}>
                <Preview
                    className={styles.itemPreview}
                    content={`${skill_type || 'skill'}_activity`}
                    image={image}
                    overlay={true}
                />
                <div className={styles.itemContent}>
                    <div className={styles.itemTop}>
                        <div className={styles.itemEvent}>{ variant === 'feed' ? this.EventString(isCurrentSubject ? overrideStatus || type : type, subject, name) : name || this.GetTranslation('unnamed') }</div>
                    </div>
                    <div className={styles.itemMid}>
                        {variant === 'feed' && <div className={styles.itemName}>
                            { skill_name || this.GetTranslation('unnamed') }
                            {hasError ? (
                                <div className={styles.itemError}>{ this.GetTranslation('error') }</div>
                            ) : (
                                <div className={styles.itemUpdated}>{ TimeSince(isCurrentSubject ? overrideUpdated || updated : updated, true) }</div>
                            )}
                        </div>}
                        {userName ? (
                            <User
                                className={styles.itemUser}
                                link={false}
                                onClick={() => this.ShowInfo(data, skill_name || this.GetTranslation('unnamed')) }
                                onLoad={this.OnUser}
                                size={20}
                                user={user}
                            >
                                {userName || this.GetTranslation('loading_user')}
                                {' '}
                                {more > 1 ? this.GetTranslation('more').replace('%s', more) : (isCurrentUser ? this.GetTranslation('onlyyou') : this.GetTranslation('nomore'))}
                            </User>
                        ) : !user ? this.GetTranslation('noone') : null}
                    </div>
                    <div className={styles.itemTray}>
                        <div className={styles.itemButtons}>
                            {href && (
                                <IconItem
                                    feather='ArrowRight'
                                    href={href}
                                    label={this.GetTranslation('readmore')}
                                    target='_blank'
                                />
                            )}
                            <IconItem
                                feather='Info'
                                id={id}
                                label={this.GetTranslation('info')}
                                onClick={() => this.ShowInfo(data, skill_name || this.GetTranslation('unnamed')) }
                            />
                            <SkillActivityStatus
                                activity={id}
                                community={contextId}
                                flip={scrollView}
                                getTranslation={this.GetTranslation}
                                onChange={status => this.OnStatusChange(id, [status, new Date().getTime()])}
                                status={overrideStatus}
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    /**
     * Load activities.
     * @return void
     */

    Load = () =>
    {
        const {filter, skills} = this.state;
        const {contextId, variant = 'feed'} = this.props;
        const endpoint = variant === 'feed' ? 'skillActivities/feed' : 'skillActivities/list';
        const skillIds = Object.keys(skills || {})
        this.setState({
            error: false,
            feed: [],
            loading: true
        });
        API.Request(endpoint, {
            community: contextId,
            filter,
            fromWidget: true,
            skills: skillIds
        }, async (response) =>
        {
            if (!this.Mounted)
            {
                return;
            }
            const {feed, list} = response;
            if (!feed && !list)
            {
                this.setState({error: true, loading: false});
            }
            else {
                this.setState({feed: feed || list, loading: false});
            }
        });
    }

    /**
     * Reset the dialog state.
     * @return void
     */

    OnCloseDialog = () =>
    {
        this.setState({dialog: null});
    }

    /**
     * Update search filter and load feed after a timeout
     * to avoid hammering the API.
     * @return void
     */

    OnFilter = (_, {skills}) =>
    {
        clearTimeout(this.FilterTimeout);
        this.setState({skills, loading: true, feed: []});
        this.FilterTimeout = setTimeout(() =>
        {
            this.Load();
        }, 300);
    }

    /**
     * Catch the scroll view on mount to use it to align the menu
     * direction in the activity status select.
     * @return void
     */

    OnScrollView = (scrollView) =>
    {
        if (!scrollView || this.state.scrollView)
        {
            return;
        }
        this.setState({scrollView: scrollView});
    }

    /**
     * Receive an updated status from the feed or info modal.
     * @return void
     */

    OnStatusChange = (activityId, status) =>
    {
        const {statuses} = this.state;
        statuses[activityId] = status;
        this.setState({statuses});
    }

    /**
     * Fetch user info from the User component to avoid redundancy.
     * @return void
     */

    OnUser = (_, user) =>
    {
       const {users} = this.state;
        users[user.id] = [user.name, user.given_name || user.name.split(' ')[0]];
        this.setState({users});
    }

    /**
     * Reload activities.
     * @return void
     */

    Reload = () =>
    {
        this.Load();
    }

    /**
     * Set the events filter label.
     * @return string - The label
     */

    SetFilterLabel = () =>
    {
        const {filter, skills} = this.state;
        const skillTitles = Object.values(skills || {})
        if (filter && skillTitles.length) {
            return `"${filter}" in ${skillTitles.join(', ')}`
        }
        if (skillTitles.length) {
            return skillTitles.join(', ')
        }
        return filter ? `"${filter}"` : 'Filter skills'
    }

    /**
     * Display the info modal for a given activity.
     * @return void
     */

    ShowInfo = (data, name) =>
    {
        const {contextId, token} = this.props;
        const {errors} = this.state;
        const {skill, ...item} = data;
        const dialog = Globals.DialogCreate({
            className: `Widget${token}`,
            props: {
                selected: item,
                skill: skill,
                community: contextId,
                error: errors[data.id],
                getTranslation: this.GetTranslation,
                onClose: this.OnCloseDialog,
                onStatus: this.OnStatusChange
            },
            sticky: true,
            title: this.GetTranslation('infotitle').replace('%s', name),
            type: 'skillActivity'
        }, data.id);
        this.setState({dialog: [dialog, data.id]});
    }

    render()
    {
        const WidgetContent = this.Content();
        const {active, contextId, hover, variant = 'feed'} = this.props;
        const {feed, filter, loading, skills} = this.state;
        const {title} = WidgetContent;
        const CA = this.ClassNames([styles.widget]);
        if (active)
        {
            CA.push('Active');
        }
        if (hover)
        {
            CA.push('Hover'); 
        }
        const Content = [];
        const Items = [];
        if (feed && feed.length)
        {
            feed.forEach((c, i) =>
            {
                Items.push(this.Item(c, i));
            });
        }
        if (!Items.length && loading)
        {
            Content.push(<Spinner
                className='WidgetSpinner'
                key='spinner'
                message={this.GetTranslation('loading')}
                overlay={true}
            />);
        }
        else if (!Items.length)
        {
            Content.push(<div
                className='WidgetEmpty'
                key='empty'
            >{this.GetTranslation(variant === 'feed' ? 'nocontent_feed' : 'nocontent_specialization')}</div>);
        }
        else
        {
            Content.push(<div
                className={styles.items}
                key='items'
            >{Items}</div>);
        }
        return (
            <div className={CA.join(' ')} ref={widget => this.RefWidget = widget} style={this.Style()}>
                {this.Toolbar()}
                <WidgetWrapper
                    {...WidgetContent}
                    className={styles.wrapper}
                    onScrollView={this.OnScrollView}
                    title={title}
                    toolbar={
                        <FilterField
                            className={styles.filter}
                            closeOnChange={true}
                            fields={{
                                skills:
                                {
                                    type: 'backenditem',
                                    community: contextId,
                                    endpoint: 'skillActivities/technical-skills',
                                    multiple: true,
                                    placeholder: 'Select technical skills...',
                                }
                            }}
                            onChange={this.OnFilter}
                            selectedLabel={this.SetFilterLabel}
                            values={{filter, skills}}
                        />
                    }
                >
                    {Content}
                </WidgetWrapper>
            </div>
        );
    }
}

export default WidgetSkillBuildingActivities;